import type { Cell, Column, EditorKind, Option, ViewBinding } from './contract.js';

export interface QueryChoice extends Option {
  /** Une personne : l'avatar, avec sa photo quand elle en a une. */
  avatar?: { src?: string | null };
  /** Archivee : elle se lit dans une puce, elle ne se propose plus. */
  archive?: boolean;
}
export interface QueryField {
  key: string;
  label: string;
  kind?: EditorKind | 'boolean';
  options?: QueryChoice[];
  /** Le champ designe une fiche (annuaire) : la valeur est son id. */
  lien?: boolean;
  /** Le champ designe une personne. */
  personne?: boolean;
  /** L'id de la personne connectee dans l'annuaire du champ : ce que « Moi » devient. */
  moi?: string;
  /** Le mot d'une valeur que l'annuaire ne connait pas, jamais l'id. */
  inconnu?: string;
}
export type FieldOperator = 'contains' | 'eq' | 'in' | 'neq' | 'gt' | 'lt' | 'empty' | 'filled';
export const VALEUR_MOI = '~moi';
const SEPARATEUR = '\n';
export const valeursDe = (value: string): string[] => value.split(SEPARATEUR).filter((item) => item !== '');
export const joindreValeurs = (values: readonly string[]): string => values.join(SEPARATEUR);
export interface FieldCondition {
  id: string;
  field: string;
  operator: FieldOperator;
  value: string;
}
export const FIELD_OPERATORS: Record<FieldOperator, string> = {
  contains: 'contient', eq: 'est', in: 'est l’un de', neq: "n’est pas", gt: 'est supérieur à',
  lt: 'est inférieur à', empty: 'est vide', filled: 'est renseigné',
};
export function queryColumns<T>(bindings: readonly ViewBinding<T>[]): Column<T>[] {
  const fields = new Map<string, Column<T>>();
  for (const binding of bindings) {
    for (const column of binding.fieldColumns ?? binding.columns ?? []) {
      if (!fields.has(column.key)) fields.set(column.key, column);
    }
  }
  return [...fields.values()];
}
export function fieldOperators(field: QueryField): FieldOperator[] {
  if (field.kind === 'number' || field.kind === 'date' || field.kind === 'datetime') {
    return ['eq', 'neq', 'gt', 'lt', 'empty', 'filled'];
  }
  if (field.kind === 'boolean') return ['eq', 'neq', 'empty', 'filled'];
  return field.options !== undefined ? ['eq', 'in', 'neq', 'empty', 'filled'] : ['contains', 'eq', 'neq', 'empty', 'filled'];
}
export function operatorLabel(operator: FieldOperator, field?: QueryField): string {
  if (field?.kind === 'date' || field?.kind === 'datetime') {
    if (operator === 'gt') return 'est après';
    if (operator === 'lt') return 'est avant';
  }
  return FIELD_OPERATORS[operator];
}
export const searchField = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
function libelleDeValeur(value: string, field: QueryField): string {
  const trouve = field.options?.find((option) => option.value === value)?.label;
  if (trouve !== undefined) return trouve;
  return value === VALEUR_MOI ? 'Moi' : field.inconnu ?? value;
}
export function conditionLabel(condition: FieldCondition, field: QueryField): string {
  const value = condition.operator === 'in'
    ? valeursDe(condition.value).map((item) => libelleDeValeur(item, field)).join(', ')
    : libelleDeValeur(condition.value, field);
  return `${field.label} ${operatorLabel(condition.operator, field)}${condition.operator === 'empty' || condition.operator === 'filled' ? '' : ` ${value}`}`;
}
export function matchesField(value: Cell, condition: FieldCondition, field: QueryField): boolean {
  const empty = value == null || value === '';
  if (condition.operator === 'empty') return empty;
  if (condition.operator === 'filled') return !empty;
  if (empty) return false;
  const text = value instanceof Date ? value.toISOString() : String(value);
  const actual = searchField(field.kind === 'date' ? text.slice(0, 10) : text);
  const expected = searchField(condition.value);
  const egal = (attendu: string): boolean => field.kind === 'multiselect' ? text.split(/[\n,]/).map((item) => item.trim()).includes(attendu)
    : field.kind === 'number' ? Number(value) === Number(attendu)
    : field.kind === 'datetime' ? new Date(text).getTime() === new Date(attendu).getTime()
    : actual === searchField(attendu);
  const attendues = (condition.operator === 'in' ? valeursDe(condition.value) : [condition.value])
    .map((item) => item === VALEUR_MOI ? field.moi : item)
    .filter((item): item is string => item !== undefined);
  const equals = attendues.some(egal);
  if (condition.operator === 'contains') return actual.includes(expected);
  if (condition.operator === 'eq' || condition.operator === 'in') return equals;
  if (condition.operator === 'neq') return !equals;
  const a = field.kind === 'number' ? Number(value) : field.kind === 'date' || field.kind === 'datetime' ? new Date(text).getTime() : actual;
  const b = field.kind === 'number' ? Number(condition.value) : field.kind === 'date' || field.kind === 'datetime' ? new Date(condition.value).getTime() : expected;
  return condition.operator === 'gt' ? a > b : a < b;
}
